import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Cloud, Zap } from "lucide-react";

const navLinks = [
  { label: "VPS", href: "/vps-hosting" },
  { label: "Cloud", href: "/cloud-hosting" },
  { label: "Shared", href: "/shared-hosting" },
  {
    label: "GPU Cloud",
    children: [
      { label: "⚡ GPU Cloud Overview", href: "/gpu-servers" },
      { label: "NVIDIA V100", href: "/gpu/v100" },
      { label: "NVIDIA A100", href: "/gpu/a100" },
      { label: "NVIDIA H200", href: "/gpu/h200" },
      { label: "RTX PRO 6000 Blackwell", href: "/gpu/rtx-blackwell" },
    ],
  },
  { label: "Bare Metal", href: "/bare-metal" },
  { label: "Managed Services", href: "/managed-services" },
  {
    label: "More",
    children: [
      { label: "Domains", href: "/domains" },
      { label: "SSL Certificates", href: "/ssl" },
      { label: "Email Hosting", href: "/email-hosting" },
      { label: "Blog", href: "/blog" },
      { label: "Refer & Earn", href: "/refer" },
      { label: "Contact Us", href: "/contact" },
    ],
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [gpuOpen, setGpuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className="fixed top-4 left-1/2 z-50 transition-all duration-300"
      style={{
        transform: "translateX(-50%)",
        width: "90%",
        maxWidth: "1200px",
        background: scrolled
          ? "rgba(3, 3, 5, 0.95)"
          : "rgba(10, 10, 18, 0.8)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: "1px solid rgba(0, 243, 255, 0.15)",
        borderRadius: "100px",
        padding: "10px 24px",
        boxShadow: scrolled ? "0 0 30px rgba(0, 243, 255, 0.1)" : "none",
      }}
    >
      <div className="flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 no-underline">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #00F3FF, #BC13FE)",
              boxShadow: "0 0 22px rgba(0, 243, 255, 0.6)",
            }}
          >
            <Cloud size={22} color="#030305" strokeWidth={2.5} />
          </div>
          <span
            className="font-orbitron font-700 text-lg tracking-wider"
            style={{
              background: "linear-gradient(to right, #00F3FF, #BC13FE)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            INDIA<span style={{ color: "#BC13FE" }}>CLOUD</span>.AI
          </span>
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.label} className="relative">
                <button
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  onClick={() => { if (link.label === "GPU Cloud") setGpuOpen(!gpuOpen); else setMoreOpen(!moreOpen); }}
                  className="flex items-center gap-1 px-4 py-2 rounded-full text-sm font-rajdhani font-600 text-gray-300 hover:text-white transition-all duration-200"
                  style={{ letterSpacing: "0.03em" }}
                >
                  {link.label}
                  <ChevronDown size={14} className={`transition-transform ${link.label === "GPU Cloud" ? (gpuOpen ? "rotate-180" : "") : (moreOpen ? "rotate-180" : "")}`} />
                </button>
                {(link.label === "GPU Cloud" ? gpuOpen : moreOpen) && (
                  <div
                    className="absolute top-full mt-2 left-0 z-50 rounded-xl overflow-hidden"
                    style={{
                      background: "rgba(10, 10, 18, 0.97)",
                      border: "1px solid rgba(0, 243, 255, 0.2)",
                      backdropFilter: "blur(20px)",
                      minWidth: "180px",
                      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(0, 243, 255, 0.1)",
                    }}
                  >
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        onClick={() => { setMoreOpen(false); setGpuOpen(false); }}
                        className="block px-5 py-3 text-sm font-outfit text-gray-300 hover:text-white hover:bg-white/5 transition-all no-underline"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className="px-4 py-2 rounded-full text-sm font-rajdhani font-600 no-underline transition-all duration-200"
                style={{
                  color: location.pathname === link.href ? "#00F3FF" : "#9ca3af",
                  background:
                    location.pathname === link.href
                      ? "rgba(0, 243, 255, 0.08)"
                      : "transparent",
                  letterSpacing: "0.03em",
                }}
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/plan-recommender"
            data-testid="nav-recommender"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-rajdhani font-700 no-underline transition-all duration-200"
            style={{
              background: "rgba(0, 243, 255, 0.1)",
              border: "1px solid rgba(0, 243, 255, 0.3)",
              color: "#00F3FF",
              letterSpacing: "0.05em",
            }}
          >
            <Zap size={12} />
            AI PICK MY PLAN
          </Link>
          <Link
            to="/vps-hosting"
            data-testid="nav-get-started"
            className="no-underline"
          >
            <button className="btn-primary text-xs px-5 py-2" style={{ fontSize: "12px", letterSpacing: "0.05em" }}>
              GET STARTED
            </button>
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden text-gray-300 hover:text-white transition-colors"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden mt-4 pb-4 border-t border-white/5 pt-4">
          <div className="flex flex-col gap-2">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label}>
                  {link.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      onClick={() => setMobileOpen(false)}
                      className="block px-3 py-2 text-sm text-gray-300 hover:text-white no-underline"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 text-sm text-gray-300 hover:text-white no-underline"
                >
                  {link.label}
                </Link>
              )
            )}
            <Link to="/plan-recommender" onClick={() => setMobileOpen(false)} className="mt-2 no-underline">
              <button className="btn-neon w-full text-sm">AI Pick My Plan</button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
